// Tiny i18n composable shared by the web-ui SPA and the browser extension. The
// UI owns localization; the backend emits stable keys (e.g. SSE `vm_phase.phase
// = "warm_adopt"`) and we look them up here against the signed-in user's BCP-47
// locale.
//
// Why not vue-i18n: one string table, no plural rules, only `{name}`-style
// interpolation. A ~30-line resolver is the right size for what we need now; the
// table can graduate later without churning callers.
//
// Locale source is a module-level ref (NOT provide/inject), mirroring
// configureClock() in datetime.ts: each app calls configureLocale() once its
// identity loads (web-ui from `auth.user.locale`, the extension from `/me`) and
// again whenever the user changes it. One locale drives every surface.

import { computed, ref, type ComputedRef } from 'vue';

import { Strings, defaultLocale, type LocaleCode } from './strings';

// The active locale tag as the app reported it (e.g. "sv-SE"), or null to follow
// the browser. Resolved to a supported primary subtag lazily in pickLocale().
const active = ref<string | null>(null);

/** Set the active locale. Called by each app once the identity loads (and after a
 *  /me PATCH). An empty/absent value means "follow the browser". */
export function configureLocale(raw: string | null | undefined): void {
  active.value = raw && raw.length > 0 ? raw : null;
}

/// Normalize a BCP-47 tag ("sv-SE", "en-US") to its primary subtag.
///
/// English (`defaultLocale`) is the default. We deliberately do NOT sniff
/// `navigator.language`: an unauthenticated visitor (sign-in / sign-up / the
/// install landing) must see English, not whatever their browser is set to.
/// Only an explicit account/user locale switches the UI - web-ui drives it from
/// `auth.user.locale` (and the /me language picker); the extension from `/me`.
function pickLocale(raw: string | null | undefined): LocaleCode {
  if (!raw || raw.length === 0) return defaultLocale;
  const primary = raw.split('-')[0]?.toLowerCase();
  return (primary && primary in Strings ? primary : defaultLocale) as LocaleCode;
}

export interface I18n {
  /// Reactive primary subtag in [en, sv, ...].
  locale: ComputedRef<LocaleCode>;
  /// Look up a stable key. Falls back to the default locale, then to the key
  /// itself - so a missing key surfaces in the UI rather than rendering as an
  /// empty string. `params` substitutes `{name}` placeholders so callers can
  /// pass dynamic values without re-implementing template literals at every
  /// call site.
  t(key: string, params?: Record<string, string | number>): string;
}

const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(PLACEHOLDER, (_, k) => {
    const v = params[k];
    return v === undefined ? `{${k}}` : String(v);
  });
}

export function useI18n(): I18n {
  const locale = computed<LocaleCode>(() => pickLocale(active.value));
  const t = (key: string, params?: Record<string, string | number>): string => {
    const table = Strings[locale.value] ?? Strings[defaultLocale];
    const raw = table[key] ?? Strings[defaultLocale][key] ?? key;
    return interpolate(raw, params);
  };
  return { locale, t };
}
